import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Col,
    Descriptions,
    Drawer,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Typography
} from 'antd';
import {
    AppstoreOutlined,
    EditOutlined,
    FolderOpenOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    SwapOutlined
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { AppState } from '../store';
import {
    getOrganizationManagementCenters,
    getOrganizationManagementInventoryMovementReasons,
    getOrganizationManagementInventoryMovements,
    getOrganizationManagementOrganizations,
    getOrganizationManagementProducts,
    getOrganizationManagementWarehouseStock,
    getOrganizationManagementWarehousesByCenter,
    postOrganizationManagementCenter,
    postOrganizationManagementInventoryMovement,
    postOrganizationManagementWarehouse,
    putOrganizationManagementCenter,
    putOrganizationManagementWarehouse
} from '../store/user/thunks';
import PatientManagement from './PatientManagement';
import AppointmentManagement from './AppointmentManagement';
import WorkflowManagement from './WorkflowManagement';
import DirectAttentionManagement from './DirectAttentionManagement';
import OrganizationSpecialityManagement from './OrganizationSpecialityManagement';
import OrganizationSettingsManagement from './OrganizationSettingsManagement';
import OrganizationContractManagement from './OrganizationContractManagement';
import OrganizationAccessManagement from './OrganizationAccessManagement';
import OrganizationProcedureManagement from './OrganizationProcedureManagement';

const { Title, Text } = Typography;

interface ManagedOrganization {
    id: string;
    name: string;
    tenant?: string | null;
    status: string;
    email?: string | null;
    phone?: string | null;
    logoUrl?: string | null;
    logoMedicalOrderUrl?: string | null;
    establishmentType?: string | null;
}

const formatEstablishmentType = (value?: string | null) => {
    const normalizedValue = value?.trim().toUpperCase();

    if (normalizedValue?.includes('CLINICA')) {
        return 'Clínica';
    }

    if (normalizedValue?.includes('CONSULTORIO')) {
        return 'Consultorio individual';
    }

    return value?.trim() || 'Consultorio individual';
};

interface ManagedCenter {
    id: string;
    organizationId: string;
    name: string;
    description?: string | null;
    address: string;
    latitud?: string | null;
    longitude?: string | null;
    status: string;
}

interface ManagedWarehouse {
    id: string;
    centerId: string;
    organizationId: string;
    centerName: string;
    code: string;
    name: string;
    observation?: string | null;
    createdAt: string;
    updatedAt?: string | null;
}

interface WarehouseStock {
    id: string;
    warehouseId: string;
    productId: string;
    productCode: string;
    productDescription: string;
    productCommercialName: string;
    quantity: number;
    minimumStock: number;
    maximumStock?: number | null;
    isLowStock: boolean;
    updatedAt?: string | null;
}

interface InventoryProduct {
    id: string;
    code: string;
    description: string;
    genericDescription: string;
    commercialName: string;
    status: string;
}

interface MovementReason {
    id: string;
    code: string;
    name: string;
    movementType: 'ENTRY' | 'EXIT';
    description?: string | null;
    status: boolean;
}

interface InventoryMovement {
    id: string;
    productCode: string;
    productDescription: string;
    reasonName: string;
    movementType: string;
    quantity: number;
    previousStock: number;
    newStock: number;
    observations?: string | null;
    createdAt: string;
    userId: string;
    userName?: string | null;
    userEmail?: string | null;
    userFullName?: string | null;
    fullName?: string | null;
    UserName?: string | null;
    UserEmail?: string | null;
}

const formatDate = (value?: string | null) => {
    if (!value) return 'Sin fecha';
    return new Date(value).toLocaleString('es-EC');
};

const buildDefaultWarehouseCode = (centerName: string | undefined, warehouses: ManagedWarehouse[]) => {
    const normalizedCenterName = (centerName || 'PRINCIPAL')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'PRINCIPAL';
    const baseCode = `BOD-${normalizedCenterName}`.slice(0, 100);
    const usedCodes = new Set(warehouses.map(warehouse => warehouse.code.trim().toUpperCase()));

    if (!usedCodes.has(baseCode)) return baseCode;

    let suffix = 2;
    let candidate = `${baseCode.slice(0, 100 - String(suffix).length - 1)}-${suffix}`;
    while (usedCodes.has(candidate)) {
        suffix += 1;
        candidate = `${baseCode.slice(0, 100 - String(suffix).length - 1)}-${suffix}`;
    }
    return candidate;
};

const OrganizationManagement: React.FC<any> = (props: any) => {
    const [centerForm] = Form.useForm();
    const [warehouseForm] = Form.useForm();
    const [movementForm] = Form.useForm();
    const [organizations, setOrganizations] = useState<ManagedOrganization[]>([]);
    const [centers, setCenters] = useState<ManagedCenter[]>([]);
    const [warehouses, setWarehouses] = useState<ManagedWarehouse[]>([]);
    const [stock, setStock] = useState<WarehouseStock[]>([]);
    const [availableStock, setAvailableStock] = useState<WarehouseStock[]>([]);
    const [products, setProducts] = useState<InventoryProduct[]>([]);
    const [reasons, setReasons] = useState<MovementReason[]>([]);
    const [movements, setMovements] = useState<InventoryMovement[]>([]);
    const [search, setSearch] = useState('');
    const [selectedOrganization, setSelectedOrganization] = useState<ManagedOrganization | null>(null);
    const [selectedCenterId, setSelectedCenterId] = useState<string>();
    const [selectedWarehouse, setSelectedWarehouse] = useState<ManagedWarehouse | null>(null);
    const [lowStockOnly, setLowStockOnly] = useState(false);
    const [centerModalOpen, setCenterModalOpen] = useState(false);
    const [warehouseModalOpen, setWarehouseModalOpen] = useState(false);
    const [movementModalOpen, setMovementModalOpen] = useState(false);
    const [editingCenter, setEditingCenter] = useState<ManagedCenter | null>(null);
    const [editingWarehouse, setEditingWarehouse] = useState<ManagedWarehouse | null>(null);
    const [movementProductLocked, setMovementProductLocked] = useState(false);
    const selectedProductId = Form.useWatch('productId', movementForm);
    const selectedMovementReasonId = Form.useWatch('inventoryMovementReasonId', movementForm);

    const selectedCenter = useMemo(
        () => centers.find(center => center.id === selectedCenterId),
        [centers, selectedCenterId]
    );

    const selectedProductStock = selectedProductId
        ? availableStock.find(item => item.productId === selectedProductId)?.quantity || 0
        : 0;
    const selectedMovementReason = reasons.find(reason => reason.id === selectedMovementReasonId);
    const selectedOrganizationIsClinic = selectedOrganization
        ? formatEstablishmentType(selectedOrganization.establishmentType) === 'Clínica'
        : false;
    const isEntryMovement = selectedMovementReason?.movementType === 'ENTRY';
    const availableMovementReasons = selectedProductId && selectedProductStock > 0
        ? reasons
        : reasons.filter(reason => reason.movementType === 'ENTRY');

    const loadOrganizations = async () => {
        const response = await props.getOrganizationManagementOrganizations(search);
        setOrganizations(response || []);
    };

    const updateOrganizationLogos = (organizationId: string, logos: Pick<ManagedOrganization, 'logoUrl' | 'logoMedicalOrderUrl'>) => {
        setOrganizations(current => current.map(organization =>
            organization.id === organizationId ? { ...organization, ...logos } : organization
        ));
        setSelectedOrganization(current => current && current.id === organizationId
            ? { ...current, ...logos }
            : current
        );
    };

    const loadCenters = async (organizationId: string) => {
        const response = await props.getOrganizationManagementCenters(organizationId);
        const loadedCenters = response || [];
        setCenters(loadedCenters);
        const firstCenterId = loadedCenters[0]?.id;
        setSelectedCenterId(firstCenterId);
        if (firstCenterId) {
            await loadWarehouses(firstCenterId);
        } else {
            setWarehouses([]);
            setSelectedWarehouse(null);
            setAvailableStock([]);
        }
    };

    const openCenterModal = (center?: ManagedCenter) => {
        setEditingCenter(center || null);
        centerForm.resetFields();
        centerForm.setFieldsValue({
            name: center?.name || '',
            description: center?.description || '',
            address: center?.address || '',
            latitud: center?.latitud || '',
            longitude: center?.longitude || '',
            status: center?.status || 'ACTIVO'
        });
        setCenterModalOpen(true);
    };

    const saveCenter = async () => {
        if (!selectedOrganization) return;
        const values = await centerForm.validateFields();
        const request = {
            name: values.name,
            description: values.description,
            address: values.address,
            latitud: values.latitud,
            longitude: values.longitude,
            status: values.status
        };

        const response = editingCenter
            ? await props.putOrganizationManagementCenter(editingCenter.id, request)
            : await props.postOrganizationManagementCenter(selectedOrganization.id, request);

        if (response) {
            setCenterModalOpen(false);
            await loadCenters(selectedOrganization.id);
        }
    };

    const loadProducts = async (organizationId: string) => {
        const response = await props.getOrganizationManagementProducts(organizationId);
        setProducts(response || []);
    };

    const loadReasons = async () => {
        const response = await props.getOrganizationManagementInventoryMovementReasons();
        setReasons(response || []);
    };

    const loadWarehouses = async (centerId: string) => {
        const response = await props.getOrganizationManagementWarehousesByCenter(centerId);
        const loadedWarehouses = response || [];
        setWarehouses(loadedWarehouses);
        const currentWarehouse = loadedWarehouses.find((warehouse: ManagedWarehouse) => warehouse.id === selectedWarehouse?.id)
            || loadedWarehouses[0]
            || null;
        setSelectedWarehouse(currentWarehouse);
        if (currentWarehouse) {
            await loadWarehouseData(currentWarehouse.id);
        } else {
            setStock([]);
            setAvailableStock([]);
            setMovements([]);
        }
    };

    const loadWarehouseData = async (warehouseId: string) => {
        const [stockResponse, movementResponse] = await Promise.all([
            props.getOrganizationManagementWarehouseStock(warehouseId, false),
            props.getOrganizationManagementInventoryMovements(warehouseId)
        ]);
        const loadedStock = stockResponse || [];
        setAvailableStock(loadedStock);
        setStock(lowStockOnly ? loadedStock.filter((item: WarehouseStock) => item.isLowStock) : loadedStock);
        setMovements(movementResponse || []);
    };

    const openManagement = async (organization: ManagedOrganization) => {
        setSelectedOrganization(organization);
        setCenters([]);
        setWarehouses([]);
        setStock([]);
        setAvailableStock([]);
        setMovements([]);
        setSelectedWarehouse(null);
        await Promise.all([
            loadCenters(organization.id),
            loadProducts(organization.id),
            loadReasons()
        ]);
    };

    const openWarehouseModal = (warehouse?: ManagedWarehouse) => {
        setEditingWarehouse(warehouse || null);
        warehouseForm.resetFields();
        const centerName = selectedCenter?.name || centers.find(center => center.id === selectedCenterId)?.name;
        warehouseForm.setFieldsValue({
            code: warehouse?.code || buildDefaultWarehouseCode(centerName, warehouses),
            name: warehouse?.name || '',
            observation: warehouse?.observation || ''
        });
        setWarehouseModalOpen(true);
    };

    const saveWarehouse = async () => {
        const values = await warehouseForm.validateFields();
        if (!selectedCenterId) {
            Modal.warning({ title: 'Centro', content: 'Seleccione un centro para registrar la bodega.' });
            return;
        }

        const request = {
            centerId: selectedCenterId,
            code: values.code,
            name: values.name,
            observation: values.observation
        };

        const response = editingWarehouse
            ? await props.putOrganizationManagementWarehouse(editingWarehouse.id, request)
            : await props.postOrganizationManagementWarehouse(selectedCenterId, request);

        if (response) {
            setWarehouseModalOpen(false);
            await loadWarehouses(selectedCenterId);
        }
    };

    const openMovementModal = (productId?: string) => {
        if (!selectedWarehouse) {
            Modal.warning({ title: 'Bodega', content: 'Seleccione una bodega para registrar movimientos.' });
            return;
        }

        setMovementProductLocked(Boolean(productId));
        const productStock = productId
            ? availableStock.find(item => item.productId === productId)
            : undefined;
        movementForm.resetFields();
        movementForm.setFieldsValue({
            productId,
            inventoryMovementReasonId: undefined,
            quantity: 1,
            minimumStock: productStock?.minimumStock ?? 0,
            maximumStock: productStock?.maximumStock ?? null
        });
        setMovementModalOpen(true);
    };

    const saveMovement = async () => {
        if (!selectedWarehouse) return;
        const values = await movementForm.validateFields();
        const selectedReason = reasons.find(reason => reason.id === values.inventoryMovementReasonId);
        const currentStock = availableStock.find(item => item.productId === values.productId)?.quantity || 0;

        if (selectedReason?.movementType === 'EXIT' && values.quantity > currentStock) {
            Modal.warning({
                title: 'Stock insuficiente',
                content: currentStock === 0
                    ? 'El producto no tiene stock registrado. Primero debe registrar una entrada.'
                    : `La salida no puede superar el stock disponible (${currentStock}).`
            });
            return;
        }

        const response = await props.postOrganizationManagementInventoryMovement(selectedWarehouse.id, {
            productId: values.productId,
            inventoryMovementReasonId: values.inventoryMovementReasonId,
            quantity: values.quantity,
            minimumStock: isEntryMovement ? (values.minimumStock ?? 0) : null,
            maximumStock: isEntryMovement ? (values.maximumStock ?? null) : null,
            observations: values.observations
        });

        if (response) {
            setMovementModalOpen(false);
            await loadWarehouseData(selectedWarehouse.id);
        }
    };

    useEffect(() => {
        loadOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedWarehouse) {
            loadWarehouseData(selectedWarehouse.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lowStockOnly]);

    const organizationColumns = [
        {
            title: 'Organizacion',
            key: 'organization',
            render: (_: unknown, row: ManagedOrganization) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.name}</Text>
                    <Text type="secondary">{row.email || 'Sin email'}</Text>
                    <Text type="secondary">Tel: {row.phone || 'Sin telefono'}</Text>
                </Space>
            )
        },
        {
            title: 'Tenant',
            dataIndex: 'tenant',
            key: 'tenant',
            width: 140,
            render: (value: string) => value || 'N/A'
        },
        {
            title: 'Tipo de establecimiento',
            dataIndex: 'establishmentType',
            key: 'establishmentType',
            width: 220,
            render: (value: string | null | undefined) => {
                const label = formatEstablishmentType(value);
                return <Tag color={label === 'Clínica' ? 'blue' : 'default'}>{label}</Tag>;
            }
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 140,
            render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'default'}>{value}</Tag>
        },
        {
            title: '',
            key: 'actions',
            width: 160,
            render: (_: unknown, row: ManagedOrganization) => (
                <Button icon={<AppstoreOutlined />} onClick={() => openManagement(row)}>
                    Gestion
                </Button>
            )
        }
    ];

    const warehouseColumns = [
        {
            title: 'Bodega',
            key: 'warehouse',
            render: (_: unknown, row: ManagedWarehouse) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.name}</Text>
                    <Text type="secondary">{row.code}</Text>
                    {row.observation && <Text type="secondary">{row.observation}</Text>}
                </Space>
            )
        },
        {
            title: 'Centro',
            dataIndex: 'centerName',
            key: 'centerName'
        },
        {
            title: 'Actualizado',
            key: 'updatedAt',
            render: (_: unknown, row: ManagedWarehouse) => formatDate(row.updatedAt || row.createdAt)
        },
        {
            title: '',
            key: 'actions',
            width: 220,
            render: (_: unknown, row: ManagedWarehouse) => (
                <Space>
                    <Button icon={<FolderOpenOutlined />} onClick={() => {
                        setSelectedWarehouse(row);
                        loadWarehouseData(row.id);
                    }}>
                        Ver stock
                    </Button>
                    <Button icon={<EditOutlined />} onClick={() => openWarehouseModal(row)} />
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>Gestion de organizaciones</Title>
            <Text type="secondary">Administra organizaciones y abre modulos de gestion operativa por organizacion.</Text>

            <div style={{ marginTop: 24, marginBottom: 18 }}>
                <Row gutter={12}>
                    <Col xs={24} md={12}>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Buscar por organizacion, email o telefono"
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            onPressEnter={loadOrganizations}
                        />
                    </Col>
                    <Col xs={24} md={4}>
                        <Button block type="primary" icon={<SearchOutlined />} onClick={loadOrganizations}>
                            Buscar
                        </Button>
                    </Col>
                    <Col xs={24} md={4}>
                        <Button block icon={<ReloadOutlined />} onClick={loadOrganizations}>
                            Actualizar
                        </Button>
                    </Col>
                </Row>
            </div>

            <Table
                rowKey="id"
                columns={organizationColumns}
                dataSource={organizations}
                pagination={{ pageSize: 10 }}
            />

            <Drawer
                open={!!selectedOrganization}
                onClose={() => setSelectedOrganization(null)}
                width="100vw"
                title={selectedOrganization ? `Gestion - ${selectedOrganization.name}` : 'Gestion'}
                destroyOnClose
            >
                {selectedOrganization && (
                    <Space direction="vertical" size={18} style={{ width: '100%' }}>
                        <Descriptions bordered size="small" column={3}>
                            <Descriptions.Item label="Organizacion">{selectedOrganization.name}</Descriptions.Item>
                            <Descriptions.Item label="Tenant">{selectedOrganization.tenant || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Tipo de establecimiento">
                                <Tag color={formatEstablishmentType(selectedOrganization.establishmentType) === 'Clínica' ? 'blue' : 'default'}>
                                    {formatEstablishmentType(selectedOrganization.establishmentType)}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Estado">
                                <Tag color={selectedOrganization.status === 'ACTIVO' ? 'green' : 'default'}>{selectedOrganization.status}</Tag>
                            </Descriptions.Item>
                        </Descriptions>

                        <Tabs
                            destroyInactiveTabPane
                            items={[
                                {
                                    key: 'warehouses',
                                    label: 'Manejo de bodegas y centros',
                                    children: (
                                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                            <Alert
                                                type="info"
                                                showIcon
                                                message="Bodegas por centro"
                                                description="Seleccione un centro para administrar sus bodegas, consultar stock y registrar movimientos de inventario."
                                            />
                                            <Row gutter={12} align="bottom">
                                                <Col xs={24} md={10}>
                                                    <Text strong>Centro</Text>
                                                    <Select
                                                        style={{ width: '100%', marginTop: 8 }}
                                                        placeholder="Seleccione un centro"
                                                        value={selectedCenterId}
                                                        options={centers.map(center => ({
                                                            value: center.id,
                                                            label: `${center.name} (${center.status})`
                                                        }))}
                                                        onChange={async value => {
                                                            setSelectedCenterId(value);
                                                            setSelectedWarehouse(null);
                                                            await loadWarehouses(value);
                                                        }}
                                                    />
                                                </Col>
                                                <Col xs={24} md={4}>
                                                    <Button
                                                        block
                                                        type="primary"
                                                        icon={<PlusOutlined />}
                                                        onClick={() => openCenterModal()}
                                                    >
                                                        Nuevo centro
                                                    </Button>
                                                </Col>
                                                <Col xs={24} md={4}>
                                                    <Button
                                                        block
                                                        icon={<EditOutlined />}
                                                        disabled={!selectedCenterId}
                                                        onClick={() => selectedCenter && openCenterModal(selectedCenter)}
                                                    >
                                                        Editar centro
                                                    </Button>
                                                </Col>
                                                <Col xs={24} md={4}>
                                                    <Button
                                                        block
                                                        icon={<ReloadOutlined />}
                                                        onClick={() => selectedOrganization && loadCenters(selectedOrganization.id)}
                                                    >
                                                        Actualizar centros
                                                    </Button>
                                                </Col>
                                            </Row>

                                            <Space wrap>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    disabled={!selectedCenterId}
                                                    onClick={() => openWarehouseModal()}
                                                >
                                                    Nueva bodega
                                                </Button>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    disabled={!selectedCenterId}
                                                    onClick={() => selectedCenterId && loadWarehouses(selectedCenterId)}
                                                >
                                                    Actualizar bodegas
                                                </Button>
                                            </Space>

                                            {!selectedCenter && <Empty description="La organizacion no tiene centros registrados." />}

                                            {selectedCenter && (
                                                <Table
                                                    rowKey="id"
                                                    columns={warehouseColumns}
                                                    dataSource={warehouses}
                                                    pagination={{ pageSize: 5 }}
                                                />
                                            )}

                                            {selectedWarehouse && (
                                                <Tabs
                                                    items={[
                                                        {
                                                            key: 'stock',
                                                            label: `Stock - ${selectedWarehouse.name}`,
                                                            children: (
                                                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                                                    <Space wrap>
                                                                        <Button type="primary" icon={<SwapOutlined />} onClick={() => openMovementModal()}>
                                                                            Registrar movimiento
                                                                        </Button>
                                                                        <Button
                                                                            icon={<ReloadOutlined />}
                                                                            onClick={() => loadWarehouseData(selectedWarehouse.id)}
                                                                        >
                                                                            Actualizar stock
                                                                        </Button>
                                                                        <Button
                                                                            type={lowStockOnly ? 'primary' : 'default'}
                                                                            onClick={() => setLowStockOnly(!lowStockOnly)}
                                                                        >
                                                                            {lowStockOnly ? 'Ver todo' : 'Solo stock bajo'}
                                                                        </Button>
                                                                    </Space>
                                                                    <Table
                                                                        rowKey="id"
                                                                        dataSource={stock}
                                                                        pagination={{ pageSize: 6 }}
                                                                        columns={[
                                                                            {
                                                                                title: 'Producto',
                                                                                key: 'product',
                                                                                render: (_: unknown, row: WarehouseStock) => (
                                                                                    <Space direction="vertical" size={0}>
                                                                                        <Text strong>{row.productDescription}</Text>
                                                                                        <Text type="secondary">{row.productCode} - {row.productCommercialName}</Text>
                                                                                    </Space>
                                                                                )
                                                                            },
                                                                            {
                                                                                title: 'Cantidad',
                                                                                dataIndex: 'quantity',
                                                                                key: 'quantity',
                                                                                align: 'right' as const
                                                                            },
                                                                            {
                                                                                title: 'Minimo',
                                                                                dataIndex: 'minimumStock',
                                                                                key: 'minimumStock',
                                                                                align: 'right' as const
                                                                            },
                                                                            {
                                                                                title: 'Maximo',
                                                                                dataIndex: 'maximumStock',
                                                                                key: 'maximumStock',
                                                                                align: 'right' as const,
                                                                                render: (value: number | null) => value ?? 'Sin maximo'
                                                                            },
                                                                            {
                                                                                title: 'Estado',
                                                                                key: 'status',
                                                                                render: (_: unknown, row: WarehouseStock) => (
                                                                                    <Tag color={row.isLowStock ? 'orange' : 'green'}>
                                                                                        {row.isLowStock ? 'Stock bajo' : 'OK'}
                                                                                    </Tag>
                                                                                )
                                                                             },
                                                                             {
                                                                                 title: '',
                                                                                 key: 'actions',
                                                                                 width: 190,
                                                                                 render: (_: unknown, row: WarehouseStock) => (
                                                                                     <Button
                                                                                         size="small"
                                                                                         icon={<SwapOutlined />}
                                                                                         onClick={() => openMovementModal(row.productId)}
                                                                                     >
                                                                                         Registrar movimiento
                                                                                     </Button>
                                                                                 )
                                                                            }
                                                                        ]}
                                                                    />
                                                                </Space>
                                                            )
                                                        },
                                                        {
                                                            key: 'movements',
                                                            label: 'Historial de movimientos',
                                                            children: (
                                                                <Table
                                                                    rowKey="id"
                                                                    dataSource={movements}
                                                                    pagination={{ pageSize: 8 }}
                                                                    columns={[
                                                                        {
                                                                            title: 'Fecha',
                                                                            dataIndex: 'createdAt',
                                                                            key: 'createdAt',
                                                                            render: formatDate
                                                                        },
                                                                        {
                                                                            title: 'Registrado por',
                                                                            key: 'userName',
                                                                            render: (_: unknown, row: InventoryMovement) => {
                                                                                const registeredBy = row.userName
                                                                                    || row.userFullName
                                                                                    || row.fullName
                                                                                    || row.UserName
                                                                                    || row.userEmail
                                                                                    || row.UserEmail;
                                                                                const registeredEmail = row.userEmail || row.UserEmail;
                                                                                return (
                                                                                    <Space direction="vertical" size={0}>
                                                                                        <Text>{registeredBy || 'Usuario no disponible'}</Text>
                                                                                        {registeredEmail && registeredEmail !== registeredBy && (
                                                                                            <Text type="secondary">{registeredEmail}</Text>
                                                                                        )}
                                                                                        {!registeredBy && row.userId && (
                                                                                            <Text type="secondary">ID: {row.userId}</Text>
                                                                                        )}
                                                                                    </Space>
                                                                                );
                                                                            }
                                                                        },
                                                                        {
                                                                            title: 'Producto',
                                                                            key: 'product',
                                                                            render: (_: unknown, row: InventoryMovement) => `${row.productCode} - ${row.productDescription}`
                                                                        },
                                                                        {
                                                                            title: 'Motivo',
                                                                            key: 'reason',
                                                                            render: (_: unknown, row: InventoryMovement) => (
                                                                                <Tag color={row.movementType === 'ENTRY' ? 'green' : 'red'}>{row.reasonName}</Tag>
                                                                            )
                                                                        },
                                                                        {
                                                                            title: 'Cantidad',
                                                                            dataIndex: 'quantity',
                                                                            key: 'quantity',
                                                                            align: 'right' as const
                                                                        },
                                                                        {
                                                                            title: 'Antes',
                                                                            dataIndex: 'previousStock',
                                                                            key: 'previousStock',
                                                                            align: 'right' as const
                                                                        },
                                                                        {
                                                                            title: 'Despues',
                                                                            dataIndex: 'newStock',
                                                                            key: 'newStock',
                                                                            align: 'right' as const
                                                                        },
                                                                        {
                                                                            title: 'Observacion',
                                                                            dataIndex: 'observations',
                                                                            key: 'observations',
                                                                            render: (value: string) => value || 'N/A'
                                                                        }
                                                                    ]}
                                                                />
                                                            )
                                                        }
                                                    ]}
                                                />
                                            )}
                                        </Space>
                                    )
                                },
                                {
                                    key: 'patients',
                                    label: 'Registro de pacientes',
                                    children: <PatientManagement organizationId={selectedOrganization.id} organizationName={selectedOrganization.name} />
                                },
                                {
                                    key: 'specialities',
                                    label: 'Especialidades',
                                    children: <OrganizationSpecialityManagement organizationId={selectedOrganization.id} organizationName={selectedOrganization.name} />
                                },
                                {
                                    key: 'procedure-services',
                                    label: 'Servicios y precios',
                                    children: <OrganizationProcedureManagement organizationId={selectedOrganization.id} />
                                },
                                {
                                    key: 'appointments',
                                    label: 'Manejo de citas',
                                    children: <AppointmentManagement organizationId={selectedOrganization.id} organizationName={selectedOrganization.name} />
                                },
                                {
                                    key: 'workflow',
                                    label: 'Workflow',
                                    children: <WorkflowManagement organizationId={selectedOrganization.id} organizationName={selectedOrganization.name} isClinic={selectedOrganizationIsClinic} />
                                },
                                ...(selectedOrganizationIsClinic ? [{
                                    key: 'direct-attention',
                                    label: 'Apertura de atención',
                                    children: <DirectAttentionManagement organizationId={selectedOrganization.id} organizationName={selectedOrganization.name} />
                                }] : []),
                                {
                                    key: 'access',
                                    label: 'Usuarios, roles y documentos',
                                    children: (
                                        <OrganizationAccessManagement
                                            organizationId={selectedOrganization.id}
                                            organizationName={selectedOrganization.name}
                                        />
                                    )
                                },
                                {
                                    key: 'settings',
                                    label: 'Ajustes',
                                    children: (
                                        <OrganizationSettingsManagement
                                            organizationId={selectedOrganization.id}
                                            organizationName={selectedOrganization.name}
                                            organizationLogoUrl={selectedOrganization.logoUrl}
                                            organizationLogoMedicalOrderUrl={selectedOrganization.logoMedicalOrderUrl}
                                            onOrganizationLogosUpdated={updateOrganizationLogos}
                                        />
                                    )
                                },
                                {
                                    key: 'contract',
                                    label: 'Contrato',
                                    children: (
                                        <OrganizationContractManagement
                                            organizationId={selectedOrganization.id}
                                            organizationName={selectedOrganization.name}
                                        />
                                    )
                                }
                            ]}
                        />
                    </Space>
                )}
            </Drawer>

            <Modal
                open={centerModalOpen}
                title={editingCenter ? 'Editar centro' : 'Nuevo centro'}
                onCancel={() => setCenterModalOpen(false)}
                onOk={saveCenter}
                okText="Guardar"
                cancelText="Cancelar"
                destroyOnClose
            >
                <Form form={centerForm} layout="vertical">
                    <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese el nombre del centro' }]}>
                        <Input placeholder="Ej. Centro principal" />
                    </Form.Item>
                    <Form.Item name="description" label="Descripcion">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="address" label="Direccion">
                        <Input placeholder="Ej. Quito" />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col xs={24} md={12}>
                            <Form.Item name="latitud" label="Latitud">
                                <Input placeholder="Ej. -0.180653" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="longitude" label="Longitud">
                                <Input placeholder="Ej. -78.467834" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="status" label="Estado">
                        <Select options={[{ value: 'ACTIVO', label: 'ACTIVO' }, { value: 'INACTIVO', label: 'INACTIVO' }]} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={warehouseModalOpen}
                title={editingWarehouse ? 'Editar bodega' : 'Nueva bodega'}
                onCancel={() => setWarehouseModalOpen(false)}
                onOk={saveWarehouse}
                okText="Guardar"
                cancelText="Cancelar"
                destroyOnClose
            >
                <Form form={warehouseForm} layout="vertical">
                    <Form.Item name="code" label="Codigo" rules={[{ required: true, message: 'Ingrese el codigo' }]}>
                        <Input placeholder="Ej. BOD-CUMBAYA" />
                    </Form.Item>
                    <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese el nombre' }]}>
                        <Input placeholder="Ej. Bodega principal" />
                    </Form.Item>
                    <Form.Item name="observation" label="Observacion">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={movementModalOpen}
                title={selectedWarehouse ? `Movimiento - ${selectedWarehouse.name}` : 'Movimiento'}
                onCancel={() => setMovementModalOpen(false)}
                onOk={saveMovement}
                okText="Registrar"
                cancelText="Cancelar"
                width={760}
                destroyOnClose
            >
                <Form form={movementForm} layout="vertical">
                    <Row gutter={12}>
                        <Col xs={24} md={12}>
                            <Form.Item name="productId" label="Producto" rules={[{ required: true, message: 'Seleccione el producto' }]}>
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Seleccione un producto"
                                    disabled={movementProductLocked}
                                    onChange={() => movementForm.setFieldValue('inventoryMovementReasonId', undefined)}
                                    popupMatchSelectWidth={460}
                                    optionRender={(option) => {
                                        const product = option.data as {
                                            code: string;
                                            description: string;
                                            label: string;
                                        };
                                        return (
                                            <div title={product.label} style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
                                                <div style={{ fontWeight: 500 }}>{product.code}</div>
                                                <div style={{ color: '#666', fontSize: 12, whiteSpace: 'normal' }}>
                                                    {product.description}
                                                </div>
                                            </div>
                                        );
                                    }}
                                    options={products.map(product => ({
                                        value: product.id,
                                        label: `${product.code} - ${product.description}`,
                                        code: product.code,
                                        description: product.description
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="inventoryMovementReasonId"
                                label="Motivo"
                                extra={selectedProductId
                                    ? `Stock actual del producto: ${selectedProductStock}`
                                    : 'Seleccione un producto para ver los motivos disponibles.'}
                                rules={[{ required: true, message: 'Seleccione el motivo' }]}
                            >
                                <Select
                                    placeholder="Seleccione el motivo"
                                    options={availableMovementReasons.map(reason => ({
                                        value: reason.id,
                                        label: `${reason.name} (${reason.movementType})`
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="quantity" label="Cantidad" rules={[{ required: true, message: 'Ingrese la cantidad' }]}>
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        {isEntryMovement && <Col xs={24} md={8}>
                            <Form.Item name="minimumStock" label="Stock minimo" rules={[{ required: true, message: 'Ingrese el minimo' }]}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        }
                        {isEntryMovement && <Col xs={24} md={8}>
                            <Form.Item name="maximumStock" label="Stock maximo">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        }
                    </Row>
                    <Form.Item name="observations" label="Observacion">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state: AppState) => ({});

export default connect(mapStateToProps, {
    getOrganizationManagementOrganizations,
    getOrganizationManagementCenters,
    getOrganizationManagementProducts,
    getOrganizationManagementWarehousesByCenter,
    postOrganizationManagementCenter,
    postOrganizationManagementWarehouse,
    putOrganizationManagementCenter,
    putOrganizationManagementWarehouse,
    getOrganizationManagementWarehouseStock,
    postOrganizationManagementInventoryMovement,
    getOrganizationManagementInventoryMovements,
    getOrganizationManagementInventoryMovementReasons
})(OrganizationManagement);
